"use client";

import { cn, Tabs, TabsList, TabsTrigger } from "@devhop/ui";
import {
  ImageIcon,
  InfoIcon,
  ShoppingCartIcon,
  StoreIcon,
  Trash2Icon,
  TrendingDownIcon,
  TrendingUpIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { AreaChart } from "@/shared/ui/AreaChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { QueryState } from "@/shared/ui/QueryState";
import type { Period } from "../api/client";
import { useShopPerformanceQuery } from "../api/queries";

const PERIODS: { value: Period; label: string }[] = [
  { value: "day", label: "ວັນ" },
  { value: "week", label: "ອາທິດ" },
  { value: "month", label: "ເດືອນ" },
];

const PERIOD_LABEL: Record<Period, string> = {
  day: "ວັນ",
  week: "ອາທິດ",
  month: "ເດືອນ",
};

const DAY_LABELS_LAO: Record<string, string> = {
  MON: "ຈັນ",
  TUE: "ອັງຄານ",
  WED: "ພຸດ",
  THU: "ພະຫັດ",
  FRI: "ສຸກ",
  SAT: "ເສົາ",
  SUN: "ອາທິດ",
};

function chartTickFormatter(period: Period): (v: string) => string {
  if (period === "day") {
    return (v) => {
      const h = Number.parseInt(v, 10);
      if (Number.isNaN(h) || h < 0 || h > 23) return v;
      return `${h}:00`;
    };
  }
  if (period === "month") {
    return (v) => `ວັນທີ ${v}`;
  }
  return (v) => DAY_LABELS_LAO[String(v)] ?? String(v);
}

function formatLak(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M LAK`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K LAK`;
  return `${n.toLocaleString()} LAK`;
}

const chartConfig: ChartConfig = {
  day: { label: "ວັນ", color: "var(--color-secondary)" },
  current: { label: "ຊຸດປະຈຸບັນ", color: "var(--color-secondary)" },
  previous: { label: "ຊຸດກ່ອນໜ້າ", color: "var(--color-muted)" },
};

export function ShopPerformancePage() {
  const [period, setPeriod] = useState<Period>("week");
  const { data, ...result } = useShopPerformanceQuery(period);

  const p = data ?? {
    totalSales: 0,
    totalSalesChangePercent: 0,
    totalOrders: 0,
    totalOrdersChangePercent: 0,
    productsSold: 0,
    productsSoldChangePercent: 0,
    activeBranches: 0,
    activeBranchesLabel: "",
    salesTrends: [],
    branchComparison: [],
  };

  const chartData =
    p.salesTrends.length > 0
      ? p.salesTrends.map((t) => ({
          day: t.day,
          current: t.current,
          previous: t.previous,
        }))
      : [{ day: "MON", current: 0, previous: 0 }];

  const maxVolume = Math.max(...p.branchComparison.map((b) => b.volume), 1);

  const periodLabel = PERIOD_LABEL[period];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຮ້ານຄ້າ", to: "/app/shops" },
            { label: "ລາຍງານການປະຕິບັດງານ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ລາຍງານການປະຕິບັດງານຂອງຮ້ານຄ້າ
            </h2>
            <p className="text-muted-foreground">
              ຕິດຕາມຍອດຂາຍ ຄຳສັ່ງ ແລະ ສາຂາທີ່ເປີດໃຫ້ບໍລິການ
            </p>
          </div>
          <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <TabsList className="rounded-lg border bg-muted p-1">
              {PERIODS.map(({ value, label }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="rounded-md px-4 py-2 font-medium text-sm data-[state=active]:shadow-sm"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <QueryState result={result} isEmpty={false}>
          {/* KPI cards */}
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border bg-card p-4">
              <div className="flex items-start justify-between">
                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ຍອດຂາຍລວມ
                </p>
                <ImageIcon className="size-4 text-primary" />
              </div>
              <p className="mt-2 font-bold text-2xl tracking-tight">
                {formatLak(p.totalSales)}
              </p>
              <div
                className={cn(
                  "mt-1 flex items-center gap-1 text-xs",
                  p.totalSalesChangePercent >= 0
                    ? "text-success"
                    : "text-destructive",
                )}
              >
                {p.totalSalesChangePercent >= 0 ? (
                  <TrendingUpIcon className="size-3.5" />
                ) : (
                  <TrendingDownIcon className="size-3.5" />
                )}
                <span>
                  {p.totalSalesChangePercent >= 0 ? "+" : ""}
                  {p.totalSalesChangePercent}% ເທົ່າກັບ {periodLabel} ກ່ອນໜ້າ
                </span>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-4">
              <div className="flex items-start justify-between">
                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ຈຳນວນຄຳສັ່ງ
                </p>
                <ShoppingCartIcon className="size-4 text-secondary" />
              </div>
              <p className="mt-2 font-bold text-2xl tracking-tight">
                {p.totalOrders.toLocaleString()}
              </p>
              <div
                className={cn(
                  "mt-1 flex items-center gap-1 text-xs",
                  p.totalOrdersChangePercent >= 0
                    ? "text-success"
                    : "text-destructive",
                )}
              >
                {p.totalOrdersChangePercent >= 0 ? (
                  <TrendingUpIcon className="size-3.5" />
                ) : (
                  <TrendingDownIcon className="size-3.5" />
                )}
                <span>
                  {p.totalOrdersChangePercent >= 0 ? "+" : ""}
                  {p.totalOrdersChangePercent}% ເທົ່າກັບ {periodLabel} ກ່ອນໜ້າ
                </span>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-4">
              <div className="flex items-start justify-between">
                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ສິນຄ້າຂາຍໄດ້
                </p>
                <Trash2Icon className="size-4 text-muted-foreground" />
              </div>
              <p className="mt-2 font-bold text-2xl tracking-tight">
                {p.productsSold.toLocaleString()}
              </p>
              <div
                className={cn(
                  "mt-1 flex items-center gap-1 text-xs",
                  p.productsSoldChangePercent >= 0
                    ? "text-success"
                    : "text-destructive",
                )}
              >
                {p.productsSoldChangePercent >= 0 ? (
                  <TrendingUpIcon className="size-3.5" />
                ) : (
                  <TrendingDownIcon className="size-3.5" />
                )}
                <span>
                  {p.productsSoldChangePercent >= 0 ? "+" : ""}
                  {p.productsSoldChangePercent}% ເທົ່າກັບ {periodLabel} ກ່ອນໜ້າ
                </span>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-4">
              <div className="flex items-start justify-between">
                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ສາຂາທີ່ເປີດ
                </p>
                <StoreIcon className="size-4 text-primary" />
              </div>
              <p className="mt-2 font-bold text-2xl tracking-tight">
                {p.activeBranches}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                {p.activeBranchesLabel === "All locations operational"
                  ? "ທຸກສາຂາເປີດໃຫ້ບໍລິການ"
                  : p.activeBranchesLabel === "No active branches"
                    ? "ບໍ່ມີສາຂາທີ່ເປີດ"
                    : p.activeBranchesLabel || "—"}
              </p>
            </div>
          </div>

          {/* Chart + Branch comparison */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="rounded-xl border bg-card p-4 lg:col-span-2">
              <h2 className="font-bold font-heading text-secondary text-sm uppercase tracking-wide">
                ແນວໂນ້ມຍອດຂາຍ
              </h2>
              <p className="text-muted-foreground text-xs">
                ການເຕີມຂຶ້ນຂອງຍອດຂາຍໃນຊ່ວງທີ່ເລືອກ
              </p>
              <div className="mt-4 flex gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="size-2.5 rounded-full bg-secondary" />
                  ຊຸດປະຈຸບັນ
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-2.5 rounded-full bg-muted" />
                  ຊຸດກ່ອນໜ້າ
                </span>
              </div>
              <div className="mt-2 h-[280px] w-full">
                <AreaChart
                  data={chartData}
                  config={chartConfig}
                  xDataKey="day"
                  valueDataKey={["current", "previous"]}
                  xAxisTickFormatter={chartTickFormatter(period)}
                  fillOpacity={0.4}
                  className="h-full w-full"
                />
              </div>
            </div>

            <div className="rounded-xl border bg-card p-4">
              <h2 className="font-bold font-heading text-secondary text-sm uppercase tracking-wide">
                ການປຽບທຽບສາຂາ
              </h2>
              <p className="text-muted-foreground text-xs">
                ສາຂາອັນດັບ 5 ຕາມຍອດຂາຍ
              </p>
              <ul className="mt-4 space-y-4">
                {p.branchComparison.map((branch) => (
                  <li key={branch.name}>
                    <p className="mb-1 truncate font-medium text-sm">
                      {branch.name}
                    </p>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-secondary transition-all"
                        style={{
                          width: `${Math.min(100, (branch.volume / maxVolume) * 100)}%`,
                        }}
                      />
                    </div>
                    <p className="mt-1 text-muted-foreground text-xs">
                      {formatLak(branch.volume)}
                    </p>
                  </li>
                ))}
              </ul>
              <p className="mt-4 flex items-center gap-1.5 text-muted-foreground text-xs">
                <InfoIcon className="size-3.5" />
                ອັບເດດລາຍງານທຸກ 15 ນາທີ
              </p>
            </div>
          </div>
        </QueryState>
      </Main>
    </>
  );
}
